import React, { useEffect, useState } from "react";
import Form from "react-bootstrap/Form";
import { fromOperatorDefault } from "Utils/Constants";
import { clickifyPhoneNumber } from "Utils/Utils";
import Linkify from "react-linkify";
import Navigation from "./Navigation";
import CancelButton from "Components/CancelButton";
import "react-datepicker/dist/react-datepicker.css";
import { Box } from "@mui/material";
import { useMediaQuery, useTheme } from "@mui/material";
import { AlertBanner, Checkbox } from "@pav/pav-products-design-system";
import useSession from "Utils/Hooks/useSession";
import { useLastEnrollmentStep } from "context/LastEnrollmentStepProvider";

const EnrollmentStep = ({
  formData,
  setForm,
  fromOperator,
  navProps,
  errorMessage,
  isEnrollment,
  plaidRedirect,
  renderFields,
  showNav = true,
  showTitle = true,
  title = "Enrollment",
  disabled = false,
}) => {
  fromOperator = fromOperator ? fromOperator : fromOperatorDefault;

  const [validated, setValidated] = useState(false);
  const [acceptedEnrollmentFee, setAcceptedEnrollmentFee] = useState(false);
  const [enrollmentFeeEnabled, setEnrollmentFeeEnabled] = useState(false);
  const [enrollmentFeeAmount, setEnrollmentFeeAmount] = useState("");
  const { session } = useSession();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Dictionary of fieldname : isValid booleans.
  const [fieldIsValid, setFieldIsValid] = useState();
  // Use to override field validation if we're on a page that doesn't show individual fields
  const [isValidOverride, setIsValidOverride] = useState();

  const onFieldChange = (fieldName, fieldValue, isValid) => {
    setForm({
      target: {
        name: fieldName,
        value: fieldValue,
        isValid: isValid,
      },
    });
    // We expect each form field to report its own validation status.
    setFieldIsValid((prevState) => {
      return { ...prevState, [fieldName]: isValid };
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const form = event.currentTarget;

    if (form.checkValidity() === false || !isFormValid) {
      event.stopPropagation();
    } else {
      navProps.navigation.next();
    }

    setValidated(true);
  };

  const isLastStep = navProps.steps.length === navProps.index + 1;
  const isFormValid =
    isValidOverride ??
    (fieldIsValid && Object.values(fieldIsValid).every((val) => val === true));
  const proceed = isFormValid;
  const updatedNavProps = { ...navProps, proceed, formData };

  const fieldProps = {
    formData,
    fromOperator,
    onFieldChange,
    disabled,
    setIsValidOverride,
    isEnrollment,
    plaidRedirect,
  };

  const { setLastEnrollmentStep } = useLastEnrollmentStep();

  useEffect(() => {
    setEnrollmentFeeEnabled(session?.merchantEnrollmentFeeEnabled);
    setEnrollmentFeeAmount(session?.merchantEnrollmentFeeAmount?.toString());
  }, [session]);

  useEffect(() => {
    setLastEnrollmentStep(isLastStep);
  }, [isLastStep]);

  return (
    <div>
      {showTitle && (
        <Box
          className="sub-title"
          sx={{
            textAlign: isMobile ? "left" : "center",
          }}
        >
          {title}
        </Box>
      )}
      {errorMessage && (
        <div className="top-error">
          <AlertBanner severity="error" title="Error">
            <Linkify
              componentDecorator={(decoratedHref, decoratedText, key) => (
                <a target="blank" href={decoratedHref} key={key}>
                  {decoratedText}
                </a>
              )}
            >
              {clickifyPhoneNumber(errorMessage)}
            </Linkify>
          </AlertBanner>
        </div>
      )}
      <Form noValidate validated={validated} onSubmit={handleSubmit}>
        {renderFields(fieldProps)}
        {showNav && (
          <Navigation
            {...updatedNavProps}
            enrollmentFeeEnabled={enrollmentFeeEnabled}
            acceptedEnrollmentFee={acceptedEnrollmentFee}
          />
        )}
      </Form>
      <CancelButton btnText="Account Sign Up" stage="Enrollment"></CancelButton>

      {isLastStep && (
        <Box>
          {enrollmentFeeEnabled && (
            <Box
              style={{
                display: "flex",
                flexDirection: "row",
                width: "100%",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "var(--caption-size-medium)",
                marginTop: "var(--spacing-md)",
                gap: "10px",
              }}
            >
              <Box className="enrollment-fee-checkbox">
                <Checkbox
                  handleCheck={(checked) => {
                    setAcceptedEnrollmentFee(checked);
                  }}
                />
              </Box>
              <Box
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <p style={{ margin: 0 }}>
                  By checking this box, you agree to pay a $
                  {enrollmentFeeAmount.split(".")[0]} enrollment fee. Pavilion
                  reserves the right to waive this fee at its sole discretion.
                </p>
              </Box>
            </Box>
          )}
        </Box>
      )}
    </div>
  );
};

export default EnrollmentStep;
